import { useEffect, useRef, useState } from 'react';

import { Eraser } from 'lucide-react';
import { Button } from '../Button/Button';

interface Props {
  name: string;
  onSubmit: (signaturePngDataUrl: string) => void;
}

export default function SignatureInput({ name, onSubmit }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isAuto, setIsAuto] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [autoSignatureUrl, setAutoSignatureUrl] = useState<string | null>(null);
  const [isCanvasEmpty, setIsCanvasEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (isAuto) {
      ctx.font = "48px 'Dancing Script', cursive";
      ctx.fillStyle = '#000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${name}`, canvas.width / 2, canvas.height / 2);
      setAutoSignatureUrl(canvas.toDataURL('image/png'));
      setIsCanvasEmpty(false);
    } else {
      setIsCanvasEmpty(isCanvasBlank(canvas));
    }
  }, [isAuto, name]);

  // Helper to check if canvas is blank
  const isCanvasBlank = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;
    const pixelBuffer = new Uint32Array(ctx.getImageData(0, 0, canvas.width, canvas.height).data.buffer);
    return !pixelBuffer.some((color) => color !== 0);
  };

  const startDrawing = (x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx?.beginPath();
    ctx?.moveTo(x, y);
    setDrawing(true);
  };

  const draw = (x: number, y: number) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx?.lineTo(x, y);
    ctx?.stroke();
    setIsCanvasEmpty(isCanvasBlank(canvas));
  };

  const stopDrawing = () => {
    setDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) setIsCanvasEmpty(isCanvasBlank(canvas));
  };

  const getScaledCoords = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * canvas.width;
    const y = ((clientY - rect.top) / rect.height) * canvas.height;
    return { x, y };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isAuto) return;
    const { x, y } = getScaledCoords(e.clientX, e.clientY);
    startDrawing(x, y);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isAuto) return;
    if (!drawing) return;
    const { x, y } = getScaledCoords(e.clientX, e.clientY);
    draw(x, y);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (isAuto) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    // Use clientX/clientY and scale to canvas coordinates
    const x = ((touch.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((touch.clientY - rect.top) / rect.height) * canvas.height;
    startDrawing(x, y);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (isAuto) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    // Use clientX/clientY and scale to canvas coordinates
    const x = ((touch.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((touch.clientY - rect.top) / rect.height) * canvas.height;
    draw(x, y);
  };

  const handleTouchEnd = () => {
    stopDrawing();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    setIsCanvasEmpty(true);
  };

  const handleSubmit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const signaturePngDataUrl = canvas.toDataURL('image/png');
    onSubmit(signaturePngDataUrl);
  };

  return (
    // <Card className="max-w-xl mx-auto p-4 shadow-2xl">
    // <CardContent className="space-y-4">
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <label className="font-semibold text-lg">Hier unterschreiben</label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`text-sm ${!isAuto ? 'text-primary-root font-bold underline cursor-default' : 'text-primary-root hover:underline'} px-1`}
            disabled={!isAuto}
            onClick={() => setIsAuto(false)}
          >
            Manuell
          </button>
          <span className="text-gray-400">|</span>
          <button
            type="button"
            className={`text-sm ${isAuto ? 'text-primary-root font-bold underline cursor-default' : 'text-primary-root hover:underline'} px-1`}
            disabled={isAuto}
            onClick={() => setIsAuto(true)}
          >
            Auto
          </button>
        </div>
      </div>

      <div className="relative border border-gray-300 rounded overflow-hidden">
        {!isAuto && (
          <button
            type="button"
            onClick={clearCanvas}
            className="absolute top-2 right-2 z-10 p-1 rounded hover:bg-gray-200 transition"
            aria-label="Clear signature"
          >
            <Eraser className="w-4 h-4" />
          </button>
        )}
        <canvas
          ref={canvasRef}
          width={500}
          height={100}
          className="w-full h-24 touch-none cursor-crosshair"
          touch-action="none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrawing}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />
      </div>

      <Button onClick={handleSubmit} className="w-full" disabled={isCanvasEmpty}>
        Abschicken
      </Button>
    </div>
    //   </CardContent>
    // </Card>
  );
}
